import { Cubit } from "blac-next";
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from "react";
import type { InternalActionSheetButton } from "@9amhealth/shared/src/molecule/ActionSheet/types";
import translate from "src/lib/translate";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { toast } from "src/state/state";
import { validateFile, type FileValidationMode } from "./fileValidation";
import type { LeapChatAttachment, LeapChatFileRef } from "./types";
import { ConversationService } from "./ConversationService";

const IS_IOS_DEVICE =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

export interface ChatComposerElements {
  textarea: HTMLTextAreaElement | null;
  cameraInput: HTMLInputElement | null;
  galleryInput: HTMLInputElement | null;
  pdfInput: HTMLInputElement | null;
  iosFileInput: HTMLInputElement | null;
}

export interface ChatComposerState {
  isDragging: boolean;
  inputValue: string;
  attachments: LeapChatAttachment[];
}

export default class ChatComposerCubit extends Cubit<ChatComposerState> {
  private service = ConversationService.getInstance();
  private onSubmit: (
    text: string,
    fileIds?: string[],
    files?: LeapChatFileRef[]
  ) => void = () => {};
  private showActionSheet: () => Promise<string> = () => Promise.resolve("");
  private isRunning = false;

  elements: ChatComposerElements = {
    textarea: null,
    cameraInput: null,
    galleryInput: null,
    pdfInput: null,
    iosFileInput: null
  };

  readonly isTouchDevice = window.matchMedia("(pointer: coarse)").matches;
  readonly isIOS = IS_IOS_DEVICE;
  readonly actionSheetButtons: InternalActionSheetButton[];

  private dragCounter = 0;

  constructor() {
    super({ isDragging: false, inputValue: "", attachments: [] });
    this.actionSheetButtons = this.buildActionSheetButtons();
  }

  private buildActionSheetButtons(): InternalActionSheetButton[] {
    const buttons: InternalActionSheetButton[] = [];
    if (this.isTouchDevice && !this.isIOS) {
      buttons.push({
        id: "camera",
        title: translate("leapChat.attachment.takePhoto"),
        icon: "camera"
      });
    }
    if (!this.isIOS) {
      buttons.push(
        {
          id: "gallery",
          title: translate(
            this.isTouchDevice
              ? "leapChat.attachment.selectFromGallery"
              : "leapChat.attachment.selectImage"
          ),
          icon: "image"
        },
        {
          id: "document",
          title: translate("leapChat.attachment.uploadFile"),
          icon: "document"
        }
      );
    }
    return buttons;
  }

  setCallbacks = (callbacks: {
    onSubmit: (
      text: string,
      fileIds?: string[],
      files?: LeapChatFileRef[]
    ) => void;
    showActionSheet: () => Promise<string>;
  }): void => {
    this.onSubmit = callbacks.onSubmit;
    this.showActionSheet = callbacks.showActionSheet;
  };

  setIsRunning = (value: boolean): void => {
    this.isRunning = value;
  };

  setElements = (elements: ChatComposerElements): void => {
    this.elements = elements;
  };

  init = (): void => {
    document.addEventListener("dragenter", this.handleDragEnter);
    document.addEventListener("dragleave", this.handleDragLeave);
    document.addEventListener("dragover", this.handleDragOver);
    document.addEventListener("drop", this.handleDrop);
  };

  dispose(): void {
    document.removeEventListener("dragenter", this.handleDragEnter);
    document.removeEventListener("dragleave", this.handleDragLeave);
    document.removeEventListener("dragover", this.handleDragOver);
    document.removeEventListener("drop", this.handleDrop);
    this.service.revokePreviewUrls(this.state.attachments);
  }

  // -- Action sheet --

  handleActionSheetSelect = (id: string): void => {
    if (id === "camera") {
      this.elements.cameraInput?.click();
    } else if (id === "gallery") {
      this.elements.galleryInput?.click();
    } else if (id === "document") {
      this.elements.pdfInput?.click();
    }
  };

  handleAttachmentPress = (): void => {
    if (this.isIOS) {
      this.elements.iosFileInput?.click();
    } else {
      void this.showActionSheet();
    }
  };

  // -- Input --

  setInputValue = (value: string): void => {
    this.patch({ inputValue: value });
  };

  handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    this.patch({ inputValue: e.target.value });
    this.autoResize();
  };

  handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Enter" && !e.shiftKey && !this.isTouchDevice) {
      e.preventDefault();
      this.submitInput();
    }
  };

  autoResize = (): void => {
    const el = this.elements.textarea;
    if (!el) {
      return;
    }
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  resetTextareaHeight = (): void => {
    const el = this.elements.textarea;
    if (el) {
      el.style.height = "auto";
    }
  };

  // -- Attachments --

  addAttachment = (file: File): void => {
    const attachment = this.service.createAttachment(file);
    this.patch({ attachments: [...this.state.attachments, attachment] });

    void this.service
      .uploadAttachmentFile(file)
      .then((fileId) => {
        this.patch({
          attachments: this.service.patchAttachmentInList(
            this.state.attachments,
            attachment.key,
            fileId ? { fileId, status: "done" } : { status: "error" }
          )
        });
      })
      .catch((err) => {
        reportErrorSentry(err);
        this.patch({
          attachments: this.service.patchAttachmentInList(
            this.state.attachments,
            attachment.key,
            { status: "error" }
          )
        });
      });
  };

  removeAttachment = (key: string): void => {
    this.patch({
      attachments: this.service.removeAttachmentFromList(
        this.state.attachments,
        key
      )
    });
  };

  private clearAttachments(): void {
    this.service.revokePreviewUrls(this.state.attachments);
    this.patch({ attachments: [] });
  }

  // -- Submit --

  get canSubmit(): boolean {
    const { attachments, inputValue } = this.state;
    const doneCount = attachments.filter((a) => a.status === "done").length;
    const hasContent = inputValue.trim().length > 0 || doneCount > 0;
    const hasUploading = attachments.some((a) => a.status === "uploading");
    const hasError = attachments.some((a) => a.status === "error");
    return hasContent && !hasUploading && !hasError && !this.isRunning;
  }

  submitInput = (): void => {
    if (!this.canSubmit) {
      return;
    }

    const text = this.state.inputValue.trim();
    const { fileIds, files } = this.service.extractAttachmentPayload(
      this.state.attachments
    );

    this.patch({ inputValue: "" });
    this.clearAttachments();
    this.resetTextareaHeight();
    this.onSubmit(text || "", fileIds, files);
  };

  // -- File handling --

  private processFiles = (
    files: Iterable<File>,
    mode: FileValidationMode
  ): void => {
    for (const file of files) {
      const error = validateFile(file, mode);
      if (error) {
        toast.error(error.key, error.variables);
        continue;
      }
      this.addAttachment(file);
    }
  };

  handleImageInput = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files) {
      this.processFiles(Array.from(e.target.files), "image");
    }
    e.target.value = "";
  };

  handlePdfInput = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files) {
      this.processFiles(Array.from(e.target.files), "pdf");
    }
    e.target.value = "";
  };

  handleCombinedInput = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files) {
      this.processFiles(Array.from(e.target.files), "any");
    }
    e.target.value = "";
  };

  handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>): void => {
    const { items } = e.clipboardData;
    if (!items) {
      return;
    }
    const imageFiles: File[] = [];
    for (const item of Array.from(items)) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      this.processFiles(imageFiles, "image");
    }
  };

  // -- Drag & drop --

  private handleDragEnter = (e: globalThis.DragEvent): void => {
    if (!e.dataTransfer?.types.includes("Files")) {
      return;
    }
    e.preventDefault();
    this.dragCounter++;
    if (this.dragCounter === 1) {
      this.patch({ isDragging: true });
    }
  };

  private handleDragLeave = (): void => {
    this.dragCounter--;
    if (this.dragCounter === 0) {
      this.patch({ isDragging: false });
    }
  };

  private handleDragOver = (e: globalThis.DragEvent): void => {
    if (!e.dataTransfer?.types.includes("Files")) {
      return;
    }
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  private handleDrop = (e: globalThis.DragEvent): void => {
    e.preventDefault();
    this.dragCounter = 0;
    this.patch({ isDragging: false });
    const files = e.dataTransfer?.files;
    if (files?.length) {
      this.processFiles(Array.from(files), "any");
    }
  };
}
